import React from 'react';
import { Box, Container, Typography, Toolbar, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BiotechIcon from '@mui/icons-material/Biotech';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LiquorIcon from '@mui/icons-material/Liquor';
import FooterSection from '../components/FooterSection';

const specs = [
  { label: 'Radio Protocol', value: 'LoRa EU 868 MHz' },
  { label: 'Measurement Frequency', value: 'Up to 1 min' },
  { label: 'Battery Type', value: 'Lithium 2600mAh (non-rechargeable)' },
  { label: 'Battery Lifetime', value: 'Up to 5 years' },
  { label: 'Probe Points per Sensor', value: '1' },
  { label: 'Working Temperature', value: '-40°C to +40°C' },
  { label: 'Temperature Accuracy', value: '±0.3°C' },
  { label: 'Measurement Technology', value: 'Non-invasive (ambient measurement)' },
  { label: 'Antenna Range', value: 'Up to 8km (line of sight in open air)' },
  { label: 'Certification', value: 'CE, UKCA certified' },
  { label: 'Safety Compliance', value: 'RoHS 3 compliant' },
  { label: 'Additional Protection', value: 'Reverse battery installation & short circuit protection' },
  { label: 'Sensor Weight', value: '69g (without battery)' },
  { label: 'Sensor Dimensions', value: '110mm × 57mm × 29mm' },
  { label: 'Sensor IP Rating', value: 'IP44' },
];

const industries = [
  {
    icon: <BiotechIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Labs & Clinics',
    description:
      'Exceptional accuracy of less than \u00b10.3\u00b0C between -40\u00b0C and 0\u00b0C, supporting strict sample integrity requirements in laboratory and clinical environments.',
  },
  {
    icon: <ScienceIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Clean Room Facilities',
    description:
      'Maintain precise environmental conditions with continuous monitoring and instant alerts, ensuring product quality and regulatory compliance in controlled spaces.',
  },
  {
    icon: <AcUnitIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Commercial Fridges & Freezers',
    description:
      'Turn your refrigeration units intelligent in hours. Open door alerts and temperature graphs give you full visibility and help meet food safety obligations.',
  },
  {
    icon: <WarehouseIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Cold Storage & Warehouse',
    description:
      'Cost-effective monitoring across large storage facilities, ensuring products are held at the correct temperature with auditable reports to boost credibility.',
  },
  {
    icon: <MedicationIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Medicine Storage',
    description:
      'Protect the integrity of pharmaceuticals and vaccines with reliable cold-chain monitoring and automatic alerts when thresholds are breached.',
  },
  {
    icon: <RestaurantIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Food & Beverage',
    description:
      'Monitor preparation and storage environments end-to-end, supporting HACCP compliance and reducing the risk of spoilage or food safety incidents.',
  },
  {
    icon: <StorefrontIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Supermarkets',
    description:
      'Continuous monitoring across refrigerated display cases and back-of-house storage, keeping stock safe and audit trails complete for environmental health visits.',
  },
  {
    icon: <LiquorIcon sx={{ fontSize: 32, color: '#004D40' }} />,
    title: 'Breweries & Wineries',
    description:
      'Maintain precise fermentation and storage temperatures to protect product quality and consistency batch after batch, with auditable records for quality assurance.',
  },
];

const EdenPage = () => {
  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#000000',
          py: { xs: 8, md: 12 },
          borderBottom: '1px solid #1a1a1a',
          overflow: 'hidden',
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src="./eden_hero.jpg"
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            filter: 'grayscale(100%) brightness(0.28)',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,77,64,0.18) 0%, rgba(0,0,0,0.55) 100%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Specification"
            size="small"
            sx={{
              fontFamily: 'Outfit',
              fontSize: '0.7rem',
              mb: 2,
              backgroundColor: '#004D40',
              color: '#ffffff',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Eden Sensor
          </Typography>
          <Typography
            component="p"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#004D40',
              mb: 1.5,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Fridge / Freezer Temperature Monitoring
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 580,
            }}
          >
            Fast deployment in seconds enables your business to switch to automated remote
            temperature monitoring in a matter of hours. IP44 rated, works reliably down to -40°C,
            with the ability to alert when temperatures go above or below set thresholds.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
            {[
              { icon: <ThermostatIcon sx={{ fontSize: 16 }} />, label: 'Down to -40°C' },
              { icon: <WaterDropOutlinedIcon sx={{ fontSize: 16 }} />, label: 'IP44 Rated' },
              { icon: <RouterOutlinedIcon sx={{ fontSize: 16 }} />, label: 'LoRaWAN' },
            ].map((b) => (
              <Box
                key={b.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: '1px solid rgba(0,77,64,0.7)',
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.75,
                  color: 'rgba(255,255,255,0.8)',
                  fontFamily: 'Outfit',
                  fontSize: '0.82rem',
                }}
              >
                {b.icon}
                {b.label}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Spec table */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Outfit',
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  color: '#000000',
                  mb: 4,
                  letterSpacing: '-0.02em',
                }}
              >
                Technical Specifications
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {specs.map((spec, i) => (
                  <Box
                    key={spec.label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 3,
                      py: 1.75,
                      backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa',
                      borderBottom: i < specs.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        color: '#444444',
                      }}
                    >
                      {spec.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: '#000000',
                      }}
                    >
                      {spec.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '3/4',
                  borderRadius: '10px',
                  border: '1px solid #ffffff',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src="./Eden_Camera_Camera_6.png"
                  alt="Eden Sensor"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Who is it for */}
      <Box sx={{ backgroundColor: '#fafafa', py: { xs: 8, md: 12 }, borderTop: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 500,
                fontSize: '0.78rem',
                color: '#004D40',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Who is Eden for?
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 700,
                fontSize: { xs: '1.9rem', md: '2.5rem' },
                color: '#000000',
                letterSpacing: '-0.02em',
              }}
            >
              Intelligent cold chain monitoring for your sector
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {industries.map((item) => (
              <Grid key={item.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box
                  sx={{
                    p: { xs: 3.5, md: 4 },
                    backgroundColor: '#ffffff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'border-color 0.25s, box-shadow 0.25s',
                    '&:hover': {
                      borderColor: '#004D40',
                      boxShadow: '0 4px 24px rgba(0,77,64,0.08)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2.5 }}>{item.icon}</Box>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#000000',
                      mb: 1.25,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 400,
                      fontSize: '0.9rem',
                      color: '#666666',
                      lineHeight: 1.75,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default EdenPage;
